import {
  BrowserRouter,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthCallback } from "./auth/AuthCallback";
import { SignInScreen } from "./auth/SignInScreen";
import { useToken } from "./auth/TokenContext";
import { Avatar } from "./components/Avatar";
import { FiltersProvider } from "./filters/FiltersContext";
import { Monthly } from "./pages/Monthly";
import { MonthlyDetail } from "./pages/MonthlyDetail";
import { Overview } from "./pages/Overview";
import { Sessions } from "./pages/Sessions";
import { Usage } from "./pages/Usage";
import { Users } from "./pages/Users";

const TABS = [
  { to: "/overview", label: "Overview" },
  { to: "/monthly", label: "Monthly" },
  { to: "/users", label: "Users" },
  { to: "/sessions", label: "Sessions" },
  { to: "/usage", label: "Usage" },
];

/**
 * Shell rendered for an authenticated session. Lives inside `BrowserRouter`
 * so the header's `useLocation` hook can compute a returnTo path for the
 * silent re-auth case.
 */
function AuthenticatedShell(): JSX.Element {
  const { session, signOut } = useToken();
  return (
    <FiltersProvider>
      <div className="app-shell">
        <header className="app-header">
          <div className="app-header-title-row">
            <h1>Adobe LLM Monitoring</h1>
            <div className="header-user">
              {session && (
                <Avatar
                  src={session.picture}
                  name={session.name}
                  email={session.email}
                  size={32}
                />
              )}
              {session?.email && (
                <span className="header-email">{session.email}</span>
              )}
              <button
                type="button"
                className="header-signout"
                onClick={signOut}
                title="Sign out and return to the sign-in screen"
              >
                Sign out
              </button>
            </div>
          </div>
          <nav
            className="app-tabs"
            role="tablist"
            aria-label="Dashboard sections"
          >
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                role="tab"
                className={({ isActive }) =>
                  isActive ? "app-tab app-tab-active" : "app-tab"
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<Overview />} />
            <Route path="/monthly" element={<Monthly />} />
            <Route path="/monthly/:yearMonth" element={<MonthlyDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/sessions" element={<Sessions />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </main>
      </div>
    </FiltersProvider>
  );
}

function UnauthenticatedSignIn(): JSX.Element {
  // Capture the path the user originally tried to load so we can land
  // them back there after the IMS round-trip.
  const location = useLocation();
  const returnTo =
    location.pathname === "/" ? "/" : `${location.pathname}${location.search}`;
  return <SignInScreen returnTo={returnTo} />;
}

export function App(): JSX.Element {
  const { session, hydrated } = useToken();

  if (!hydrated) {
    return <div className="app-loading">Loading…</div>;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="*"
          element={session ? <AuthenticatedShell /> : <UnauthenticatedSignIn />}
        />
      </Routes>
    </BrowserRouter>
  );
}
